import { useEffect, useMemo, useState } from "react";
import { api } from "../api";
import { GiveUp } from "../components/GiveUp";
import { ResultSheet } from "../components/ResultSheet";
import { Centered, Header, Shell } from "../components/Shell";
import { SuggestInput } from "../components/SuggestInput";
import { Toast } from "../components/Toast";
import { useGame } from "../useGame";

interface Guess {
  name: string;
  by: number;
  byDir: "earlier" | "later" | "same";
  byClose?: boolean;
  cty: string;
  ctyUk?: string;
  ctyOk: boolean;
  regionOk?: boolean;
  occ: string;
  occOk: boolean;
  g: string;
  gOk: boolean;
  alive: boolean;
  aliveOk: boolean;
}

interface View {
  guesses: Guess[];
  /** Free hints that unlock as misses pile up (2 → 4 → 6 → 8). */
  hints: { occ?: string; cty?: string; decade?: string; letter?: string };
  answer?: { uk: string; by: number; dy: number | null; cty: string; occ: string };
}

const COPY = {
  unknown_person: "Не знаю такої людини — вибери з підказок",
  already_guessed: "Вже було",
};

/** ok = exact match, warm = close (same continent, ±15 років), no = cold. */
function Chip({ tone, children }: { tone: "ok" | "warm" | "no"; children: React.ReactNode }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${
        tone === "ok"
          ? "bg-ok/20 text-ok"
          : tone === "warm"
            ? "bg-warm/20 text-warm"
            : "bg-[rgba(128,128,128,0.15)] text-tghint"
      }`}
    >
      {children}
      {tone === "ok" ? " ✓" : ""}
    </span>
  );
}

export function Persondle({ date }: { date?: string | undefined }) {
  const g = useGame<View>("persondle", COPY, date);
  const [names, setNames] = useState<{ uk: string; en: string }[]>([]);

  useEffect(() => {
    void api.names("persondle").then((r) => setNames(r.names)).catch(() => {});
  }, []);

  const state = g.screen.kind === "game" ? g.screen.state : null;
  const finished = state !== null && state.status !== "playing";
  const guessed = useMemo(() => new Set(state?.view.guesses.map((x) => x.name) ?? []), [state?.view.guesses]);

  if (g.screen.kind === "loading") return <Shell><Centered>…</Centered></Shell>;
  if (g.screen.kind === "error") {
    return (
      <Shell>
        <Centered>
          <div>{g.screen.message}</div>
          {g.screen.retry && (
            <button type="button" onClick={() => void g.load()} className="rounded-xl bg-tgbutton px-5 py-2.5 text-sm font-semibold text-tgbuttontext">
              ще раз
            </button>
          )}
        </Centered>
      </Shell>
    );
  }
  const s = g.screen.state;
  const v = s.view;
  const won = s.status === "won";

  return (
    <Shell header={<Header left={date ? `📼 ${date}` : `#${s.number}`} title="ХТО ЦЕ?" right={date ? "" : `🔥 ${s.me.streak}`} />}>
      <div className="flex-1 overflow-y-auto px-4 pb-2">
        <div className="mb-2 text-center text-xs text-tghint">
          вгадай відому людину — кожна спроба показує, наскільки близько
        </div>
        {!finished && (v.hints.occ || v.hints.cty || v.hints.decade || v.hints.letter) && (
          <div className="mb-2 flex flex-wrap justify-center gap-1.5">
            {v.hints.occ && (
              <span className="rounded-full bg-warm/20 px-2.5 py-1 text-[11px] font-medium">💡 {v.hints.occ}</span>
            )}
            {v.hints.cty && (
              <span className="rounded-full bg-warm/20 px-2.5 py-1 text-[11px] font-medium">💡 {v.hints.cty}</span>
            )}
            {v.hints.decade && (
              <span className="rounded-full bg-warm/20 px-2.5 py-1 text-[11px] font-medium">💡 {v.hints.decade}</span>
            )}
            {v.hints.letter && (
              <span className="rounded-full bg-warm/20 px-2.5 py-1 text-[11px] font-medium">💡 на «{v.hints.letter}»</span>
            )}
          </div>
        )}
        {v.guesses.length === 0 && (
          <div className="mt-8 text-center text-sm text-tghint">почни з когось дуже відомого 🕵️</div>
        )}
        <ul className="space-y-2">
          {[...v.guesses].reverse().map((x) => {
            const exact = won && x.name === v.guesses.at(-1)?.name && x.ctyOk && x.occOk;
            return (
              <li key={x.name} className={`rounded-xl px-3 py-2 ${exact ? "bg-ok/15" : "bg-tgsection"}`}>
                <div className="text-sm font-semibold">{x.name}</div>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  <Chip tone={x.byDir === "same" ? "ok" : x.byClose ? "warm" : "no"}>
                    🎂 {x.by}
                    {x.byDir === "earlier"
                      ? `${x.byClose ? " · гаряче," : " ·"} шукай ранішу`
                      : x.byDir === "later"
                        ? `${x.byClose ? " · гаряче," : " ·"} шукай пізнішу`
                        : ""}
                  </Chip>
                  <Chip tone={x.ctyOk ? "ok" : x.regionOk ? "warm" : "no"}>
                    🌍 {x.ctyUk ?? x.cty}
                    {x.regionOk ? " · той самий континент" : ""}
                  </Chip>
                  <Chip tone={x.occOk ? "ok" : "no"}>💼 {x.occ}</Chip>
                  <Chip tone={x.gOk ? "ok" : "no"}>{x.g === "F" ? "жінка" : x.g === "M" ? "чоловік" : "стать: ?"}</Chip>
                  <Chip tone={x.aliveOk ? "ok" : "no"}>{x.alive ? "живе" : "🪦"}</Chip>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
      <div style={{ paddingBottom: "max(var(--tg-safe-area-inset-bottom, 0px), 10px)" }}>
        <SuggestInput
          names={names}
          disabled={finished}
          busy={g.busy}
          exclude={guessed}
          onSubmit={(name) => void g.run(() => api.guess<View>("persondle", name, date))}
          placeholder="ім'я або прізвище"
        />
        {!finished && v.guesses.length >= 3 && (
          <div className="mt-2 flex px-4">
            <GiveUp busy={g.busy} onGiveUp={() => void g.run(() => api.giveUp<View>("persondle", date))} />
          </div>
        )}
      </div>

      <Toast message={g.toast} />
      {g.sheetOpen && finished && (
        <ResultSheet
          state={s}
          archive={Boolean(date)}
          title={won ? "Впізнав!" : "Не сьогодні"}
          subtitle={won ? `за ${v.guesses.length} · стрік ${s.me.streak} 🔥` : "стрік згорів"}
          scoreLabel="avg спроб"
          formatScore={(n) => `${n}`}
          onDismiss={() => g.setSheetOpen(false)}
        >
          {v.answer && (
            <div className="mt-4 rounded-xl bg-[rgba(128,128,128,0.12)] px-4 py-3 text-center">
              <div className="text-lg font-bold">{v.answer.uk}</div>
              <div className="mt-1 text-xs text-tghint">
                {v.answer.by}
                {v.answer.dy ? `–${v.answer.dy}` : ""} · {v.answer.cty} · {v.answer.occ}
              </div>
            </div>
          )}
        </ResultSheet>
      )}
    </Shell>
  );
}
